import { type CSSProperties, type ReactNode, useEffect, useState } from "react";

const loadingOverlayStyles = {
  background:
    "var(--product-viewer-loading-background, var(--background, Canvas))",
  foreground:
    "var(--product-viewer-loading-foreground, var(--foreground, CanvasText))",
  gridSection:
    "var(--product-viewer-loading-grid-section, color-mix(in srgb, currentColor 28%, transparent))",
  gridFine:
    "var(--product-viewer-loading-grid-fine, color-mix(in srgb, currentColor 14%, transparent))",
};

// HUD-style corner bracket for the framed loading panel.
const HudCorner = ({
  style,
  rotate,
}: {
  style: CSSProperties;
  rotate: 0 | 90 | 180 | 270;
}) => (
  <svg
    style={{
      ...style,
      height: 20,
      opacity: 0.7,
      position: "absolute",
      transform: `rotate(${rotate}deg)`,
      width: 20,
    }}
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M1 8 V1 H8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="square"
    >
      <animate
        attributeName="stroke-dasharray"
        values="0 14;14 0"
        dur="450ms"
        fill="freeze"
      />
    </path>
  </svg>
);

// Isometric box assembling itself from exploded panels.
// Coordinates are arranged so the three visible faces (top / left / right)
// snap into place from outside the viewport into a unit cube at the center.
const AssemblyStage = () => (
  <div style={{ height: 176, position: "relative", width: 176 }}>
    {/* soft glow under the converging assembly */}
    <div
      style={{
        backgroundColor: "currentColor",
        borderRadius: 999,
        bottom: 24,
        filter: "blur(40px)",
        height: 24,
        left: 32,
        opacity: 0.15,
        position: "absolute",
        right: 32,
      }}
    />

    <svg
      style={{
        bottom: 0,
        height: "100%",
        left: 0,
        position: "absolute",
        right: 0,
        top: 0,
        width: "100%",
      }}
      viewBox="0 0 200 200"
      aria-labelledby="assembly-stage-title"
    >
      <title id="assembly-stage-title">Bauteile werden montiert</title>

      <g transform="translate(100 100) scale(1.12) translate(-100 -100)">
        {/* ghost wireframe of the final cube – pre-drawn outline that pulses */}
        <g
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.4"
          strokeWidth="1"
          strokeDasharray="3 4"
        >
          <polygon points="100,64 131,82 100,100 69,82" />
          <polygon points="131,118 100,136 100,100 131,82" />
          <polygon points="69,118 100,136 100,100 69,82" />
          <animate
            attributeName="opacity"
            values="0.25;0.7;0.25"
            dur="1200ms"
            repeatCount="indefinite"
          />
        </g>

        {/* assembled faces – each enters from a different direction and locks in */}
        <g
          fill="currentColor"
          fillOpacity="0.2"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <polygon points="100,64 131,82 100,100 69,82">
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0 -55; 0 0; 0 0"
              keyTimes="0; 0.7; 1"
              dur="1100ms"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0; 1; 1; 0"
              keyTimes="0; 0.55; 0.92; 1"
              dur="1100ms"
              repeatCount="indefinite"
            />
          </polygon>
        </g>

        <g
          fill="currentColor"
          fillOpacity="0.12"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <polygon points="131,118 100,136 100,100 131,82">
            <animateTransform
              attributeName="transform"
              type="translate"
              values="55 28; 0 0; 0 0"
              keyTimes="0; 0.7; 1"
              dur="1100ms"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0; 1; 1; 0"
              keyTimes="0; 0.7; 0.92; 1"
              dur="1100ms"
              repeatCount="indefinite"
            />
          </polygon>
        </g>

        <g
          fill="currentColor"
          fillOpacity="0.08"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <polygon points="69,118 100,136 100,100 69,82">
            <animateTransform
              attributeName="transform"
              type="translate"
              values="-55 28; 0 0; 0 0"
              keyTimes="0; 0.7; 1"
              dur="1100ms"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0; 1; 1; 0"
              keyTimes="0; 0.85; 0.92; 1"
              dur="1100ms"
              repeatCount="indefinite"
            />
          </polygon>
        </g>
      </g>
    </svg>
  </div>
);

// Monospaced status readout with a blinking caret.
const AssemblyReadout = () => (
  <div
    style={{
      alignItems: "center",
      display: "flex",
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 10,
      gap: 8,
      letterSpacing: "0.25em",
      opacity: 0.8,
      textTransform: "uppercase",
    }}
  >
    <span
      style={{
        animation: "product-viewer-loading-pulse 2s ease-in-out infinite",
        backgroundColor: "currentColor",
        borderRadius: 999,
        height: 6,
        width: 6,
      }}
    />
    <span>Montage läuft</span>
    <span style={{ display: "inline-block", textAlign: "left", width: 12 }}>
      <span
        style={{
          animation: "product-viewer-loading-pulse 2s ease-in-out infinite",
        }}
      >
        _
      </span>
    </span>
  </div>
);

// Two-tier blueprint grid that mirrors the GridFloorScene three.js grid:
// fine cells (~#ccc) every small step, section lines (~#999) every 5 cells,
// and a heavier main line (~#666) every 10 cells, all with a radial fade.
const BlueprintGrid = () => (
  <div
    style={{
      backgroundImage: [
        // section lines (#999 equivalent) – the only emphasised tier
        `linear-gradient(${loadingOverlayStyles.gridSection} 1px, transparent 1px)`,
        `linear-gradient(90deg, ${loadingOverlayStyles.gridSection} 1px, transparent 1px)`,
        // fine cell lines (#ccc equivalent) – barely there
        `linear-gradient(${loadingOverlayStyles.gridFine} 1px, transparent 1px)`,
        `linear-gradient(90deg, ${loadingOverlayStyles.gridFine} 1px, transparent 1px)`,
      ].join(","),
      backgroundSize: ["84px 84px", "84px 84px", "14px 14px", "14px 14px"].join(
        ",",
      ),
      backgroundPosition: "0 0",
      WebkitMaskImage:
        "radial-gradient(circle at center, black 10%, transparent 70%)",
      bottom: 0,
      left: 0,
      maskImage:
        "radial-gradient(circle at center, black 10%, transparent 70%)",
      position: "absolute",
      right: 0,
      top: 0,
    }}
    aria-hidden="true"
  />
);

export const ProductViewerLoadingOverlay = ({
  children,
  visible,
}: {
  children?: ReactNode;
  visible: boolean;
}) => {
  const [shouldRender, setShouldRender] = useState(visible);

  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      return;
    }

    const timeout = globalThis.setTimeout(() => {
      setShouldRender(false);
    }, 700);

    return () => globalThis.clearTimeout(timeout);
  }, [visible]);

  if (!shouldRender) return null;

  return (
    <div
      style={{
        alignItems: "center",
        backdropFilter: "blur(4px)",
        backgroundColor: loadingOverlayStyles.background,
        bottom: 0,
        color: loadingOverlayStyles.foreground,
        display: "flex",
        justifyContent: "center",
        left: 0,
        opacity: visible ? 1 : 0,
        pointerEvents: "none",
        position: "absolute",
        right: 0,
        top: 0,
        transition: "opacity 700ms ease-out",
        zIndex: 10,
      }}
      aria-hidden={!visible}
    >
      <style>
        {`
          @keyframes product-viewer-loading-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.35; }
          }
        `}
      </style>
      <BlueprintGrid />

      {children ?? (
        // framed HUD panel
        <div
          style={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 20,
            padding: "32px 40px",
            position: "relative",
          }}
        >
          <HudCorner style={{ left: 0, top: 0 }} rotate={0} />
          <HudCorner style={{ right: 0, top: 0 }} rotate={90} />
          <HudCorner style={{ bottom: 0, right: 0 }} rotate={180} />
          <HudCorner style={{ bottom: 0, left: 0 }} rotate={270} />

          <AssemblyStage />
          <AssemblyReadout />
        </div>
      )}
    </div>
  );
};
